import { deleteSession } from '../_helpers/sessions';
import { store } from "../stores/store";
import { logout, updateUser } from '../actions/auth';
import { axiosInstance } from "./axios";
import {SERVICE_WS} from "./servicesHanu";
import config  from '../_helpers/config';

const X_API_KEY = config.get('xApiKey');

let subscribers = [];
let isAlreadyFetchingRefreshToken = false;


function _removeSession() {
    store.dispatch(logout());
    deleteSession();
}


function _removePendingRequests() {
    subscribers = [];
    isAlreadyFetchingRefreshToken = false;
}


function _resumePendingRequests() {
    isAlreadyFetchingRefreshToken = false;
    subscribers = subscribers.filter(callback => callback());
}


async function _handleRefreshTokenRequest(token) {
    try {
        const rtReqInstance = axiosInstance().create();
        const response = await rtReqInstance.post('/v1/users/refresh-token', { token });

        const { newToken: access_token, refresh_token } = response.data;
        store.dispatch(updateUser({ token: access_token, refresh_token }));

        _resumePendingRequests();
    } catch (error) {
        const status = error?.response?.status;

        if (status >= 400 && status < 500) {
            _removePendingRequests();
            _removeSession();
        }
    }
}


class APIClient {
    constructor({
        endpoint = '',
        data = {},
        requestConfig = {},
        service = SERVICE_WS,
    } = {}) {
        this.service = service;
        this.endpoint = endpoint;
        this.data = data;
        this.requestConfig = requestConfig;
        this.axiosInstance = axiosInstance(this.service).create();
        this.requestInterceptor = null;
        this.responseInterceptor = null;
    }


    startInterceptors() {
        this.requestInterceptor = this.axiosInstance.interceptors.request.use(
            config => {
                this.setCustomHeaders(config.headers);

                return config;
            },
            error => Promise.reject(error),
        );

        this.responseInterceptor = this.axiosInstance.interceptors.response.use(
            response => response,
            error => this.handleErrorResponseInterceptor(error),
        );
    }


    stopInterceptors() {
        this.axiosInstance.interceptors.request.eject(this.requestInterceptor);
        this.axiosInstance.interceptors.response.eject(this.responseInterceptor);
    }


    setCustomHeaders(headers) {
        const { authAdmin, auth, geolocation, language } = store.getState();

        headers['client_country_code'] = geolocation.country_code;
        headers['client_ip'] = geolocation.IPv4;

        if (auth.isAuthenticated) {
            headers['Authorization'] = `JWT ${auth.user.token}`;
            headers['x-api-key'] = X_API_KEY;
        } else if (authAdmin.isAuthenticatedAdmin) {
            headers['Authorization'] = `JWT ${authAdmin.admin.access_token}`;
        }

        if (language.code) {
            headers['language'] = language.code;
        }
    }


    async fetchRefreshToken() {
        isAlreadyFetchingRefreshToken = true;

        const { auth } = store.getState();
        const { refresh_token = '' } = auth.user;

        if (refresh_token) {
            await _handleRefreshTokenRequest(refresh_token);
        } else {
            _removePendingRequests();
            _removeSession();
        }
    }


    retryOriginalRequest(originalConfig) {
        return new Promise(resolve => {
            // Restarts every previous failed request after fetching the refresh token
            subscribers.push(() => resolve(this.axiosInstance(originalConfig)));
        });
    }


    async handleErrorResponseInterceptor(error) {
        const { response = null, config: originalConfig } = error;
        const { auth } = store.getState();

        // Refresh session token if user is still logged in
        if (response?.status === 401 && auth.isAuthenticated) {
            const retryOriginalRequest = this.retryOriginalRequest(originalConfig);

            if (!isAlreadyFetchingRefreshToken) {
                await this.fetchRefreshToken();
            }

            return retryOriginalRequest;
        }

        return Promise.reject(error);
    }


    delete() {
        this.startInterceptors();

        return this.axiosInstance.delete(this.endpoint, this.requestConfig)
            .finally(() => this.stopInterceptors());
    }

    get() {
        this.startInterceptors();

        return this.axiosInstance.get(this.endpoint, this.requestConfig)
            .finally(() => this.stopInterceptors());
    }


    post() {
        this.startInterceptors();

        return this.axiosInstance.post(this.endpoint, this.data, this.requestConfig)
            .finally(() => this.stopInterceptors());
    }


    patch() {
        this.startInterceptors();

        return this.axiosInstance.patch(this.endpoint, this.data, this.requestConfig)
            .finally(() => this.stopInterceptors());
    }
}


export default APIClient;
